import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Tooltip } from '@mui/material';

interface HeatMapDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

const categoryNames: Record<string, { hebrew: string; english: string; icon: string }> = {
  'management': { hebrew: 'ניהול', english: 'Management', icon: '🎯' },
  'organization': { hebrew: 'ארגון', english: 'Organization', icon: '🏢' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales', icon: '🔄' },
  'finance': { hebrew: 'כספים', english: 'Finance', icon: '💰' },
  'operations': { hebrew: 'תפעול', english: 'Operations', icon: '⚙️' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control', icon: '✅' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales', icon: '📈' },
  'stress': { hebrew: 'מתח', english: 'Stress', icon: '😰' },
  'principles': { hebrew: 'עקרונות', english: 'Principles', icon: '🧭' },
  'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help', icon: '🤝' },
};

const HeatMapDashboard: React.FC<HeatMapDashboardProps> = ({ categoryScores, language }) => {
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he' ? categoryNames[categoryKey]?.hebrew : categoryNames[categoryKey]?.english,
        icon: categoryNames[categoryKey]?.icon || '📊',
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),
      personal: scores.slice(7, 10),
      all: scores,
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
      overallAverage: scores.reduce((sum, cat) => sum + cat.value, 0) / scores.length
    };
  }, [categoryScores, language]);

  const getHeatColor = (score: number) => {
    // Red to Yellow to Green gradient
    if (score >= 80) return '#27ae60';
    if (score >= 70) return '#2ecc71';
    if (score >= 60) return '#52c77c';
    if (score >= 50) return '#f39c12';
    if (score >= 40) return '#e67e22';
    if (score >= 30) return '#e74c3c';
    return '#c0392b';
  };

  const getTextColor = (score: number) => {
    return score >= 50 ? 'white' : 'white';
  };

  const getScoreLabel = (score: number) => {
    if (language === 'he') {
      if (score >= 80) return 'מצוין';
      if (score >= 60) return 'טוב';
      if (score >= 40) return 'בינוני';
      return 'חלש';
    } else {
      if (score >= 80) return 'Excellent';
      if (score >= 60) return 'Good';
      if (score >= 40) return 'Fair';
      return 'Poor';
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
        {language === 'he' ? '🗺️ מפת חום - תצוגת מטריצה' : '🗺️ Heat Map - Matrix View'}
      </Typography>

      {/* Overall Score */}
      <Paper
        elevation={4}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${getHeatColor(processedScores.overallAverage)}dd, ${getHeatColor(processedScores.overallAverage)})`,
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          {language === 'he' ? 'ציון כולל' : 'Overall Score'}
        </Typography>
        <Typography variant="h2" fontWeight="bold">
          {processedScores.overallAverage.toFixed(1)}%
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, opacity: 0.95 }}>
          {getScoreLabel(processedScores.overallAverage)}
        </Typography>
      </Paper>

      {/* Category Heat Map Grid */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
          🏢 {language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}
        </Typography>
        <Grid container spacing={2}>
          {processedScores.business.map((category) => (
            <Grid item xs={6} sm={4} md={3} key={category.key}>
              <Tooltip title={`${category.name}: ${category.value.toFixed(0)}% - ${getScoreLabel(category.value)}`} arrow>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    backgroundColor: getHeatColor(category.value),
                    color: getTextColor(category.value),
                    textAlign: 'center',
                    height: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 6
                    }
                  }}
                >
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {category.icon}
                  </Typography>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: '0.85rem' }}>
                    {category.name}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {category.value.toFixed(0)}%
                  </Typography>
                </Paper>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
          👤 {language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}
        </Typography>
        <Grid container spacing={2}>
          {processedScores.personal.map((category) => (
            <Grid item xs={6} sm={4} key={category.key}>
              <Tooltip title={`${category.name}: ${category.value.toFixed(0)}% - ${getScoreLabel(category.value)}`} arrow>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    backgroundColor: getHeatColor(category.value),
                    color: getTextColor(category.value),
                    textAlign: 'center',
                    height: '140px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 6
                    }
                  }}
                >
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {category.icon}
                  </Typography>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: '0.85rem' }}>
                    {category.name}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {category.value.toFixed(0)}%
                  </Typography>
                </Paper>
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Summary Row */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              background: `linear-gradient(135deg, ${getHeatColor(processedScores.businessAverage)}dd, ${getHeatColor(processedScores.businessAverage)})`,
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
              🏢 {language === 'he' ? 'ממוצע עסקי' : 'Business Average'}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {processedScores.businessAverage.toFixed(1)}%
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              background: `linear-gradient(135deg, ${getHeatColor(processedScores.personalAverage)}dd, ${getHeatColor(processedScores.personalAverage)})`,
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
              👤 {language === 'he' ? 'ממוצע אישי' : 'Personal Average'}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {processedScores.personalAverage.toFixed(1)}%
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Color Scale Legend */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="body2" fontWeight="600" sx={{ mb: 2, textAlign: 'center' }}>
          {language === 'he' ? 'סולם צבעים' : 'Color Scale'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { min: 0, max: 30, color: '#c0392b', label: '0-30%' },
            { min: 30, max: 40, color: '#e74c3c', label: '30-40%' },
            { min: 40, max: 50, color: '#e67e22', label: '40-50%' },
            { min: 50, max: 60, color: '#f39c12', label: '50-60%' },
            { min: 60, max: 70, color: '#52c77c', label: '60-70%' },
            { min: 70, max: 80, color: '#2ecc71', label: '70-80%' },
            { min: 80, max: 100, color: '#27ae60', label: '80-100%' },
          ].map((range) => (
            <Box key={range.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 30, height: 20, backgroundColor: range.color, border: '1px solid #ccc' }} />
              <Typography variant="caption">{range.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HeatMapDashboard;
